import { useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { DartInput } from '../types/darts';
import { useAppTheme } from '../hooks/useAppTheme';
import { useT } from '../hooks/useT';

interface InputKeypadPerDartProps {
  onAddDart: (dart: DartInput) => void;
  onRemoveLastDart: () => void;
  currentTurnDarts: DartInput[];
  isCricket?: boolean;
}

const MAX_DARTS = 3;

const triggerHaptic = () => {
  try {
    navigator.vibrate?.(10);
  } catch {
    // trillen is een extraatje
  }
};

export function InputKeypadPerDart({ onAddDart, onRemoveLastDart, currentTurnDarts, isCricket = false }: InputKeypadPerDartProps) {
  const { t } = useT();
  const { theme, isDark } = useAppTheme();
  const [multiplier, setMultiplier] = useState<1 | 2 | 3>(1);
  const full = currentTurnDarts.length >= MAX_DARTS;

  const commitDart = (value: number, mult: 1 | 2 | 3) => {
    if (full) return;
    triggerHaptic();
    // Bull kan niet triple: dan telt hij als dubbel.
    const actualMult = value === 25 && mult === 3 ? 2 : mult;
    const label =
      value === 0 ? '0' : value === 25 ? (actualMult === 2 ? 'D-Bull' : 'Bull') : actualMult === 3 ? `T${value}` : actualMult === 2 ? `D${value}` : `${value}`;
    onAddDart({ multiplier: actualMult, value, score: value * actualMult, label });
    setMultiplier(1);
  };

  const toggleMultiplier = (mult: 2 | 3) => {
    triggerHaptic();
    setMultiplier(prev => (prev === mult ? 1 : mult));
  };

  const rowHeight = isCricket ? 'min-h-11' : 'min-h-12';
  const numberButton = `w-full rounded-2xl font-mono font-bold transition-all duration-150 active:scale-95 disabled:opacity-30 ${
    isCricket ? 'min-h-11 text-lg' : 'aspect-square text-xl sm:text-2xl'
  } ${isDark ? theme.keypadNumberBg : 'bg-white shadow-sm'} ${theme.keypadNumberHover} ${theme.keypadNumberActiveBg} ${theme.keypadNumberText}`;
  const multiplierButton = (active: boolean) =>
    `${rowHeight} rounded-2xl text-sm font-bold whitespace-nowrap transition-all duration-150 active:scale-95 ${
      active
        ? `${theme.multiplierActiveBg} ${theme.multiplierActiveText}`
        : isDark
        ? `${theme.multiplierInactiveBg} ${theme.multiplierInactiveText}`
        : `bg-white shadow-sm ${theme.multiplierInactiveText}`
    }`;

  return (
    <div className="mx-auto w-full max-w-xl space-y-2.5 rounded-3xl bg-black/5 p-3 backdrop-blur-xl dark:bg-black/40 sm:p-4">
      <div className="flex min-h-12 items-center justify-between rounded-2xl bg-white px-3 py-1.5 shadow-sm dark:bg-white/5">
        <div className="flex items-center gap-2" aria-live="polite">
          <span className="text-xs font-semibold text-slate-600 uppercase dark:text-slate-400">{t('dartsLabel')}</span>
          <div className="flex gap-1.5 font-mono">
            {[0, 1, 2].map(slot => {
              const dart = currentTurnDarts[slot];
              return (
                <span
                  key={slot}
                  className={`flex h-7 w-10 items-center justify-center rounded-xl text-xs font-bold ${
                    dart ? `${theme.keypadPrimaryBg} ${theme.keypadPrimaryText}` : 'bg-black/5 text-slate-500 dark:bg-white/5 dark:text-slate-400'
                  }`}
                >
                  {dart ? dart.label : '–'}
                </span>
              );
            })}
          </div>
          {currentTurnDarts.length > 0 && (
            <span className="font-mono text-sm font-bold">= {currentTurnDarts.reduce((acc, d) => acc + d.score, 0)}</span>
          )}
        </div>

        {/* Blijft in de layout (onzichtbaar) zodat de balk niet verspringt */}
        <button
          type="button"
          onClick={() => {
            triggerHaptic();
            onRemoveLastDart();
          }}
          tabIndex={currentTurnDarts.length > 0 ? 0 : -1}
          aria-hidden={currentTurnDarts.length === 0}
          className={`flex min-h-11 shrink-0 items-center gap-1.5 rounded-2xl bg-amber-500/15 px-3 text-sm font-semibold whitespace-nowrap text-amber-900 transition-all duration-150 active:scale-95 dark:text-amber-200 ${
            currentTurnDarts.length > 0 ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <RotateCcw size={16} aria-hidden />
          {t('removeDart')}
        </button>
      </div>

      <div className="grid grid-cols-3 gap-1.5">
        <button type="button" onClick={() => toggleMultiplier(2)} aria-pressed={multiplier === 2} className={multiplierButton(multiplier === 2)}>
          {t('multDouble')}
        </button>
        <button type="button" onClick={() => toggleMultiplier(3)} aria-pressed={multiplier === 3} className={multiplierButton(multiplier === 3)}>
          {t('multTriple')}
        </button>
        <button
          type="button"
          disabled={full}
          onClick={() => commitDart(0, 1)}
          className={`${rowHeight} rounded-2xl bg-red-500/15 text-sm font-bold whitespace-nowrap text-red-800 transition-all duration-150 active:scale-95 disabled:opacity-30 dark:bg-red-950/50 dark:text-red-300`}
        >
          {t('miss')}
        </button>
      </div>

      <div className="grid grid-cols-5 gap-1.5">
        {Array.from({ length: 20 }, (_, i) => i + 1).map(num => (
          <button key={num} type="button" disabled={full} onClick={() => commitDart(num, multiplier)} className={numberButton}>
            {num}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-1.5">
        <button
          type="button"
          disabled={full}
          onClick={() => commitDart(25, 1)}
          className={`${rowHeight} rounded-2xl text-sm font-bold whitespace-nowrap shadow-sm transition-all duration-150 active:scale-95 disabled:opacity-30 ${theme.bullBg} ${theme.bullText}`}
        >
          {t('bull')}
        </button>
        <button
          type="button"
          disabled={full}
          onClick={() => commitDart(25, 2)}
          className={`${rowHeight} rounded-2xl text-sm font-bold whitespace-nowrap shadow-sm transition-all duration-150 active:scale-95 disabled:opacity-30 ${theme.dbullBg} ${theme.dbullText}`}
        >
          {t('doubleBull')}
        </button>
      </div>
    </div>
  );
}
